"use client";

import { FC, ReactElement, ReactNode } from "react";
import {
  chakra,
  // eslint-disable-next-line no-restricted-imports
  Dialog as ChakraDialog,
  DialogRootProps,
  Portal,
  Flex,
  CloseButton,
} from "@chakra-ui/react";
import {
  dialogHeaderRecipe,
  dialogContentRecipe,
  dialogBodyRecipe,
  dialogFooterRecipe,
} from "./dialog.recipe";

type Props = {
  trigger?: ReactElement;
  header?: string;
  subHeader?: ReactNode;
  actions?: ReactElement[];
  autoHeight?: boolean;
  maxWidth?: number;
  initialFocusEl?: () => HTMLElement | null;
  onClose: VoidFunction;
} & DialogRootProps;

export const DialogRoot = ChakraDialog.Root;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogHeader = chakra(ChakraDialog.Header, dialogHeaderRecipe);
export const DialogBody = chakra(ChakraDialog.Body, dialogBodyRecipe);
export const DialogContent = chakra(ChakraDialog.Content, dialogContentRecipe);
export const DialogFooter = chakra(ChakraDialog.Footer, dialogFooterRecipe);
export const DialogPositioner = ChakraDialog.Positioner;
export const DialogCloseTrigger = ChakraDialog.CloseTrigger;

const Dialog: FC<Props> = ({
  trigger,
  header,
  subHeader,
  actions = [],
  children,
  autoHeight,
  maxWidth,
  initialFocusEl,
  onClose,
  ...props
}) => (
  <DialogRoot
    scrollBehavior="inside"
    closeOnInteractOutside
    closeOnEscape
    initialFocusEl={initialFocusEl}
    onOpenChange={(details: { open: boolean }) => {
      if (!details.open) {
        onClose();
      }
    }}
    {...props}
  >
    {trigger}

    <Portal>
      <DialogBackdrop />

      <DialogPositioner>
        <DialogContent
          height={autoHeight ? "auto" : undefined}
          maxWidth={maxWidth}
        >
          <DialogHeader>
            <DialogCloseTrigger asChild>
              <CloseButton
                onClick={onClose}
                color="currentColor"
                _hover={{ bg: "none" }}
              />
            </DialogCloseTrigger>

            <div>
              {header && <h2>{header}</h2>}
              {subHeader}
            </div>
          </DialogHeader>

          <DialogBody visual={header ? undefined : "withoutTitle"}>
            {children}
          </DialogBody>

          {Boolean(actions.length) && (
            <DialogFooter>
              <Flex gap={2}>{actions}</Flex>
            </DialogFooter>
          )}
        </DialogContent>
      </DialogPositioner>
    </Portal>
  </DialogRoot>
);

export default Dialog;
